import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import {
    getRankingStatus,
    uploadResume,
    rankResumes,
    rankResumesFromFile,
    cancelRanking as apiCancelRanking,
    pauseRanking as apiPauseRanking,
    resumeRanking as apiResumeRanking,
} from "../services/api";
import { getErrorMessage } from "../lib/errors";

// Progress-bar animation tick interval (ms). The trickle loop fires this often
// to advance the displayed value smoothly between the 2s backend status polls.
const TRICKLE_MS = 200;

// ─── Helpers for sessionStorage persistence ────────────────────────────────
const SS_PREFIX = "rankingCtx_";

function ssGet(key, fallback) {
    try {
        const raw = sessionStorage.getItem(SS_PREFIX + key);
        if (raw === null) return fallback;
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function ssSet(key, value) {
    try {
        sessionStorage.setItem(SS_PREFIX + key, JSON.stringify(value));
    } catch {
        // storage quota exceeded – silently ignore
    }
}

// ─── Upload result flattening ──────────────────────────────────────────────
// /api/upload returns one entry per *submitted* item. A plain file rejects at
// the top level, but a ZIP rejects per member: the ZIP entry carries
// status "success"/"partial" with the individual verdicts nested under `files`
// (see backend/app/routers/resumes.py). Walking only the top level therefore
// misses every rejection inside a ZIP — which is why a ZIP upload used to
// report "N skipped" without naming a single file. Returns every refused file
// as { file, reason } so the UI can list them all.
const DEFAULT_REASON = "Could not be processed.";

function collectUploadProblems(results) {
    const problems = [];

    const pushEntry = (entry, zipName) => {
        if (!entry || (entry.status !== "rejected" && entry.status !== "error")) return;
        const name = entry.file || "Unknown file";
        problems.push({
            // Qualify ZIP members with their archive so a bare "resume.pdf" is
            // traceable back to the file the user actually dropped.
            file: zipName ? `${zipName} → ${name}` : name,
            reason: entry.reason || entry.error || DEFAULT_REASON,
        });
    };

    (results || []).forEach((entry) => {
        if (!entry) return;
        const nested = Array.isArray(entry.files) ? entry.files : null;
        if (nested && nested.length > 0) {
            // A ZIP: the per-member verdicts are the useful detail. The parent's
            // own status ("partial", or a generic "All files failed to upload")
            // adds nothing on top of them.
            nested.forEach((child) => pushEntry(child, entry.file));
            return;
        }
        // A plain file, or a ZIP rejected as a whole (bad archive, zip-bomb
        // guard, no processable members) — no per-member detail exists.
        pushEntry(entry);
    });

    return problems;
}

// ─── Context ───────────────────────────────────────────────────────────────
const RankingContext = createContext(null);

export function RankingProvider({ children }) {
    // ── Persistent form state (survives navigation AND browser refresh) ──
    const [jobDescription, setJobDescriptionRaw] = useState(() => ssGet("jobDescription", ""));
    const [jdMode, setJdModeRaw] = useState(() => ssGet("jdMode", "text")); // "text" | "file"
    const [rankMode, setRankModeRaw] = useState(() => ssGet("rankMode", "ai")); // "ai" | "economy"
    const [fitThreshold, setFitThresholdRaw] = useState(() => ssGet("fitThreshold", [70]));
    const [topN, setTopNRaw] = useState(() => ssGet("topN", [20]));

    // Ref holds the latest jobDescription without being a dep of the setter callback.
    // Updated synchronously inside setJobDescription so the ref is always current.
    const jobDescriptionRef = useRef(jobDescription);
    // Debounce timer for sessionStorage write so we don't write on every keystroke.
    const jdSaveTimerRef = useRef(null);

    // Wrap setters to also write to sessionStorage
    const setJobDescription = useCallback((v) => {
        const val = typeof v === "function" ? v(jobDescriptionRef.current) : v;
        jobDescriptionRef.current = val;
        setJobDescriptionRaw(val);
        // Debounce sessionStorage write — avoids a synchronous write per keystroke
        clearTimeout(jdSaveTimerRef.current);
        jdSaveTimerRef.current = setTimeout(() => ssSet("jobDescription", val), 300);
    }, []); // empty deps — ref keeps value fresh without recreating the callback

    const setJdMode = useCallback((v) => {
        setJdModeRaw(v);
        ssSet("jdMode", v);
    }, []);

    const setRankMode = useCallback((v) => {
        setRankModeRaw(v);
        ssSet("rankMode", v);
    }, []);

    const setFitThreshold = useCallback((v) => {
        const val = typeof v === "function" ? v(fitThreshold) : v;
        setFitThresholdRaw(val);
        ssSet("fitThreshold", val);
    }, [fitThreshold]);

    const setTopN = useCallback((v) => {
        const val = typeof v === "function" ? v(topN) : v;
        setTopNRaw(val);
        ssSet("topN", val);
    }, [topN]);

    // ── In-memory state (survives navigation, lost on refresh) ──
    const [files, setFiles] = useState([]);                   // File[] – cannot serialise
    const [jdFile, setJdFile] = useState(null);               // File | null
    const [uploadedCount, setUploadedCount] = useState(0);
    const [currentSessionId, setCurrentSessionId] = useState(null);

    // ── Upload state ───────────────────────────────────────────────────────
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    // Every file the last upload refused, as { file, reason }. A toast is too
    // small and too short-lived to name 20 rejected CVs, so the full list lives
    // here and is rendered as a persistent panel the user dismisses themselves.
    const [rejectedFiles, setRejectedFiles] = useState([]);

    // ── Ranking state ──────────────────────────────────────────────────────
    const [isRanking, setIsRanking] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [rankingProgress, setRankingProgress] = useState(0);
    const [rankingStage, setRankingStage] = useState("");
    const [rankingError, setRankingError] = useState(null);
    const [rankingComplete, setRankingComplete] = useState(false);
    // Wall-clock seconds since the current run started. A ticking timer is the
    // clearest "still working" signal and is always truthful — it drives the
    // `Ranking · 1:12` readout next to the progress bar.
    const [rankingElapsedSec, setRankingElapsedSec] = useState(0);
    const runStartRef = useRef(null);

    // Ref for the polling interval so we can clear it from anywhere
    const pollIntervalRef = useRef(null);
    // Ref for the in-flight upload AbortController so we can cancel from anywhere
    const uploadAbortRef = useRef(null);
    // Ref to the latest pushToast so the polling callback can surface notices
    const pushToastRef = useRef(null);
    // Synchronous re-entry guard: blocks a rapid second click before the async
    // isRanking state has propagated (the backend also rejects concurrent runs
    // with 409, this just avoids issuing the duplicate request at all).
    const isSubmittingRef = useRef(false);

    // ── Progress-bar animation refs ──────────────────────────────────────────
    // The bar shows an eased/trickling value while the backend is polled only
    // every 2s. `realTargetRef` holds the true backend-reported percentage; a
    // ~200ms trickle loop moves the displayed value toward it and gently creeps
    // during long LLM calls so the bar never looks frozen. `pausedRef` freezes
    // the creep while the pipeline is paused (mirrors the isPaused state without
    // needing it as a trickle-effect dependency).
    const realTargetRef = useRef(0);
    const trickleIntervalRef = useRef(null);
    const pausedRef = useRef(false);
    // Bounds of the CURRENT stage's band (backend `band_start` / `band_end`) and
    // the resume count, used to pace a continuous fill across each band. The bar
    // advances steadily across the band over the stage's expected duration (the
    // pipeline estimates ~6s/resume), so it keeps moving even when a stage is a
    // single opaque LLM call that reports no sub-progress (which would otherwise
    // pin the raw backend value at the band start). Defaults keep the very first
    // pre-poll motion inside the first stage's band.
    const bandStartRef = useRef(0);
    const bandEndRef = useRef(15);
    const totalRef = useRef(1);

    // ── Polling logic ──────────────────────────────────────────────────────
    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (trickleIntervalRef.current) {
            clearInterval(trickleIntervalRef.current);
            trickleIntervalRef.current = null;
        }
    }, []);

    // Continuously animate the bar between the 2s polls. Runs every ~200ms; the
    // CSS `transition-all` on <Progress> turns each small step into fluid motion.
    // Monotonic (never moves backward), capped at 95% until the backend reports
    // completion.
    //
    // The displayed value is driven by the larger of two motions, so the bar is
    // always moving:
    //   1. Fast catch-up to the last *real* backend value (`realTargetRef`) — shows
    //      genuine jumps as batches/chunks land in stages that report sub-progress.
    //   2. A steady, time-paced fill across the CURRENT stage's band, sized so the
    //      bar crosses the band over the stage's expected duration (~6s/resume,
    //      apportioned by band width). This is what keeps the bar moving during a
    //      single opaque LLM call that reports no sub-progress — the case that
    //      previously pinned it at the raw band start (15% parsing, 40% ranking).
    // The fill is confined to the current band, so it never jumps ahead into the
    // next stage; if a stage overruns its estimate the bar eases to the band
    // ceiling and waits there (reads as "finishing up") until the next stage lifts
    // the ceiling. A `total`-based pace means a 9-resume run fills in seconds while
    // a 177-resume run fills over minutes, matching real durations.
    const startTrickle = useCallback(() => {
        if (trickleIntervalRef.current) return;
        trickleIntervalRef.current = setInterval(() => {
            setRankingProgress((prev) => {
                const ceiling = Math.min(95, bandEndRef.current);
                if (prev >= ceiling) return prev; // hold at band ceiling until the stage advances
                let next = prev;
                // (1) Fast catch-up to a real backend milestone if it's ahead.
                const target = realTargetRef.current;
                if (prev < target) {
                    next = prev + (target - prev) * 0.25;
                }
                // (2) Steady time-paced fill across the current band.
                if (!pausedRef.current) {
                    const width = Math.max(1, ceiling - bandStartRef.current);
                    // Expected ticks to cross this band: 6s per resume for the whole
                    // pipeline, apportioned by band width. Floored so even a tiny run
                    // still takes a few seconds per band rather than snapping across.
                    const expectedTicks = Math.max(20, (6000 * Math.max(1, totalRef.current) * (width / 100)) / TRICKLE_MS);
                    next = Math.max(next, prev + width / expectedTicks);
                }
                return Math.min(ceiling, Math.max(prev, next));
            });
        }, TRICKLE_MS);
    }, []);

    const startPolling = useCallback(() => {
        startTrickle(); // animate the bar smoothly between the 2s polls
        if (pollIntervalRef.current) return; // guard: already polling, don't create a second interval
        pollIntervalRef.current = setInterval(async () => {
            try {
                const res = await getRankingStatus();
                const data = res.data;

                if (data.status === "complete") {
                    realTargetRef.current = 100;
                    setRankingProgress(100);
                    setRankingStage("Complete");
                    setIsRanking(false);
                    setIsPaused(false);
                    pausedRef.current = false;
                    setRankingComplete(true);
                    stopPolling();
                } else if (data.status === "no_changes") {
                    // JD and resume set are unchanged since the last ranking: the
                    // backend skipped re-ranking and charged nothing. Existing
                    // results stay on screen; just tell the user why nothing ran.
                    realTargetRef.current = 100;
                    setRankingProgress(100);
                    setRankingStage("No changes");
                    setIsRanking(false);
                    setIsPaused(false);
                    pausedRef.current = false;
                    setRankingComplete(true);
                    stopPolling();
                    pushToastRef.current?.({
                        type: "info",
                        message: data.message || "No changes in JD or resumes since the last ranking. No credits used.",
                    });
                } else if (data.status === "error") {
                    setRankingError(data.error || "Ranking failed");
                    setIsRanking(false);
                    setIsPaused(false);
                    pausedRef.current = false;
                    realTargetRef.current = 0;
                    setRankingProgress(0);
                    stopPolling();
                } else if (data.status === "cancelled") {
                    setIsRanking(false);
                    setIsPaused(false);
                    pausedRef.current = false;
                    realTargetRef.current = 0;
                    setRankingProgress(0);
                    setRankingStage("");
                    stopPolling();
                } else if (data.status === "paused") {
                    // Pipeline is paused at a stage boundary. Keep isRanking true so
                    // the progress UI sticks around, and surface the paused flag.
                    // pausedRef freezes the trickle creep so the bar holds steady.
                    setIsPaused(true);
                    pausedRef.current = true;
                    setRankingStage(data.stage ? `Paused: ${data.stage}` : "Paused");
                    // Keep polling so we observe the transition back to processing on resume.
                } else if (data.status === "processing") {
                    const { progress = 0, total = 1, stage = "Processing", overall, band_start, band_end } = data;
                    // Prefer the backend's global 0-100 `overall` (monotonic across
                    // stages); fall back to the per-stage fraction for older payloads.
                    const pct = typeof overall === "number"
                        ? Math.min(95, overall)
                        : (total > 0 ? Math.min(95, Math.round((progress / total) * 90) + 5) : 10);
                    // Feed the trickle loop rather than snapping the bar directly;
                    // guard monotonic in case a stale poll reports a lower value.
                    realTargetRef.current = Math.max(realTargetRef.current, pct);
                    // Give the time-paced fill the current stage's band + resume count.
                    // Monotonic — a stale out-of-order poll for an earlier stage must
                    // not shrink the band or rewind the pace.
                    if (typeof band_start === "number") bandStartRef.current = Math.max(bandStartRef.current, band_start);
                    if (typeof band_end === "number") bandEndRef.current = Math.max(bandEndRef.current, band_end);
                    if (typeof total === "number" && total > 0) totalRef.current = total;
                    setRankingStage(stage);
                    setIsPaused(false);
                    pausedRef.current = false;
                }
                // status === "idle" → ranking not yet kicked off on backend, keep waiting
            } catch (err) {
                // Network error – silently retry on next tick
                console.warn("Ranking status poll error:", err);
            }
        }, 2000);
    }, [stopPolling, startTrickle]);

    // Clean up interval on unmount (Provider unmounts with the whole app)
    useEffect(() => () => stopPolling(), [stopPolling]);

    // Tick the elapsed-time readout once a second while a run is active.
    useEffect(() => {
        if (!isRanking) return;
        if (!runStartRef.current) runStartRef.current = Date.now();
        const id = setInterval(() => {
            setRankingElapsedSec(Math.floor((Date.now() - runStartRef.current) / 1000));
        }, 1000);
        return () => clearInterval(id);
    }, [isRanking]);

    // ── Upload action ──────────────────────────────────────────────────────
    const handleUploadResumes = useCallback(async (pushToast) => {
        if (files.length === 0) {
            pushToast?.({ type: "error", message: "Please select at least one resume to upload" });
            return;
        }
        setIsUploading(true);
        setUploadProgress(10);
        // Drop the previous run's rejections so the panel never mixes two uploads.
        setRejectedFiles([]);
        const controller = new AbortController();
        uploadAbortRef.current = controller;
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append("resume", file));
            setUploadProgress(50);
            const uploadResp = await uploadResume(formData, { signal: controller.signal });
            if (uploadResp.status !== 200) throw new Error("Resume upload failed");
            setUploadProgress(100);

            // The endpoint returns HTTP 200 even when some files are rejected
            // (e.g. an incompatible/corrupt ZIP), so inspect the per-file results.
            const data = uploadResp.data || {};
            const stats = data.statistics || {};
            const uploaded = stats.uploaded ?? 0;
            const problems = collectUploadProblems(data.results);
            // Prefer the real number of named rejections over the top-level
            // statistics, which count one ZIP as a single "rejected" item no
            // matter how many resumes inside it were refused.
            const skipped = problems.length || (stats.rejected ?? 0) + (stats.failed ?? 0);

            // Hand the full list to the UI, which names every file in a panel the
            // user dismisses themselves. The toast only carries the count and
            // points at that panel — it is 320px wide and auto-dismisses in 7s,
            // so it cannot be where a 20-name list lives.
            setRejectedFiles(problems);

            if (uploaded > 0) {
                setUploadedCount((prev) => prev + uploaded);
                setFiles([]);
                if (skipped > 0) {
                    pushToast?.({
                        type: "warning",
                        message: `Uploaded ${uploaded} file(s); ${skipped} skipped. See the list below.`,
                    });
                } else {
                    pushToast?.({ type: "success", message: `Successfully uploaded ${uploaded} file(s)!` });
                }
            } else {
                // Nothing was uploaded — keep the queue so the user can fix and retry.
                pushToast?.({
                    type: "error",
                    message: skipped > 0
                        ? `Upload failed: ${skipped} file(s) skipped. See the list below.`
                        : (data.message || "Upload failed: no compatible files found."),
                });
            }
        } catch (error) {
            // Treat AbortError as a clean cancel, not a failure.
            const isAbort = error?.name === "CanceledError" || error?.name === "AbortError" || error?.code === "ERR_CANCELED";
            if (isAbort) {
                pushToast?.({ type: "info", message: "Upload cancelled" });
            } else {
                console.error("Upload error:", error);
                pushToast?.({ type: "error", message: getErrorMessage(error, "Upload failed") });
            }
        } finally {
            uploadAbortRef.current = null;
            setIsUploading(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    }, [files]);

    const clearRejectedFiles = useCallback(() => setRejectedFiles([]), []);

    const cancelUpload = useCallback(() => {
        if (uploadAbortRef.current) {
            uploadAbortRef.current.abort();
            uploadAbortRef.current = null;
        }
    }, []);

    // ── Ranking action ─────────────────────────────────────────────────────
    const handleRankResumes = useCallback(async (pushToast) => {
        // Re-entry guard: ignore a second click while a submit is already in flight.
        if (isSubmittingRef.current || isRanking) {
            pushToast?.({ type: "info", message: "A ranking is already running. Please wait for it to finish." });
            return;
        }
        if (uploadedCount === 0 && files.length === 0) {
            pushToast?.({ type: "error", message: "Please upload at least one resume before ranking" });
            return;
        }
        if (files.length > 0) {
            pushToast?.({ type: "info", message: "You have un-uploaded files in your queue. Please upload them or remove them before ranking." });
            return;
        }
        if (jdMode === "text" && !jobDescription.trim()) {
            pushToast?.({ type: "error", message: "Please enter a job description" });
            return;
        }
        if (jdMode === "file" && !jdFile) {
            pushToast?.({ type: "error", message: "Please upload a job description file" });
            return;
        }

        // Make pushToast reachable from the polling callback (for the no_changes notice).
        pushToastRef.current = pushToast;
        isSubmittingRef.current = true;
        setIsRanking(true);
        realTargetRef.current = 5;
        bandStartRef.current = 0; // start in the first stage's band
        bandEndRef.current = 15;
        totalRef.current = uploadedCount || files.length || 1; // best guess until the first poll reports the real count
        pausedRef.current = false;
        runStartRef.current = Date.now();
        setRankingElapsedSec(0);
        setRankingProgress(5);
        setRankingStage("Starting...");
        setRankingError(null);
        setRankingComplete(false);

        try {
            let res;
            if (jdMode === "text") {
                res = await rankResumes(jobDescription, fitThreshold[0], rankMode);
            } else {
                const jdFormData = new FormData();
                jdFormData.append("jd_file", jdFile);
                jdFormData.append("fit_threshold", fitThreshold[0]);
                jdFormData.append("ranking_mode", rankMode);
                res = await rankResumesFromFile(jdFormData);
            }
            // Synchronous small-batch path may already report "no changes".
            if (res?.data?.no_changes) {
                setRankingProgress(100);
                setRankingStage("No changes");
                setRankingComplete(true);
                setIsRanking(false);
                pushToast?.({
                    type: "info",
                    message: res.data.message || "No changes in JD or resumes since the last ranking. No credits used.",
                });
                return;
            }
            // API returned → ranking is now running (or complete for small batches)
            pushToast?.({ type: "success", message: "Ranking pipeline started!" });
            // Start polling for real-time progress
            startPolling();
        } catch (error) {
            console.error("Pipeline error:", error);
            // 409 = a ranking for this session is already running (concurrent submit).
            if (error?.response?.status === 409) {
                pushToast?.({
                    type: "info",
                    message: getErrorMessage(error, "A ranking for this session is already running. Please wait for it to finish."),
                });
            } else {
                pushToast?.({ type: "error", message: getErrorMessage(error, "Processing failed") });
            }
            setIsRanking(false);
            setRankingProgress(0);
            setRankingStage("");
        } finally {
            isSubmittingRef.current = false;
        }
    }, [uploadedCount, files, jdMode, jobDescription, jdFile, fitThreshold, rankMode, startPolling, isRanking]);

    const resetRanking = useCallback(() => {
        stopPolling();
        setIsRanking(false);
        setIsPaused(false);
        pausedRef.current = false;
        realTargetRef.current = 0;
        bandStartRef.current = 0;
        bandEndRef.current = 15;
        totalRef.current = 1;
        runStartRef.current = null;
        setRankingElapsedSec(0);
        setRankingProgress(0);
        setRankingStage("");
        setRankingError(null);
        setRankingComplete(false);
    }, [stopPolling]);

    const cancelRanking = useCallback(async (pushToast) => {
        try {
            await apiCancelRanking();
            pushToast?.({ type: "info", message: "Cancelling ranking..." });
            // Polling will observe status='cancelled' and reset state.
        } catch (error) {
            console.error("Cancel ranking error:", error);
            pushToast?.({ type: "error", message: getErrorMessage(error, "Failed to cancel ranking") });
        }
    }, []);

    const pauseRanking = useCallback(async (pushToast) => {
        try {
            await apiPauseRanking();
            pushToast?.({ type: "info", message: "Pausing at next stage boundary..." });
            // Polling will observe status='paused' and set isPaused.
        } catch (error) {
            console.error("Pause ranking error:", error);
            pushToast?.({ type: "error", message: getErrorMessage(error, "Failed to pause ranking") });
        }
    }, []);

    const resumeRanking = useCallback(async (pushToast) => {
        try {
            await apiResumeRanking();
            setIsPaused(false);
            setRankingStage("Resuming...");
            // Ensure polling is active to track the resumed run.
            startPolling();
            pushToast?.({ type: "success", message: "Ranking resumed" });
        } catch (error) {
            console.error("Resume ranking error:", error);
            pushToast?.({ type: "error", message: getErrorMessage(error, "Failed to resume ranking") });
        }
    }, [startPolling]);

    const value = {
        // Form state
        jobDescription, setJobDescription,
        jdMode, setJdMode,
        rankMode, setRankMode,
        jdFile, setJdFile,
        fitThreshold, setFitThreshold,
        topN, setTopN,
        files, setFiles,
        uploadedCount, setUploadedCount,
        currentSessionId, setCurrentSessionId,
        // Upload state
        isUploading,
        uploadProgress,
        rejectedFiles,
        clearRejectedFiles,
        // Ranking state
        isRanking,
        isPaused,
        rankingProgress,
        rankingStage,
        rankingError,
        rankingComplete,
        rankingElapsedSec,
        // Actions
        handleUploadResumes,
        cancelUpload,
        handleRankResumes,
        cancelRanking,
        pauseRanking,
        resumeRanking,
        resetRanking,
    };

    return (
        <RankingContext.Provider value={value}>
            {children}
        </RankingContext.Provider>
    );
}

export function useRankingContext() {
    const ctx = useContext(RankingContext);
    if (!ctx) throw new Error("useRankingContext must be used within a RankingProvider");
    return ctx;
}
